// Song library shared types: what the hub stores about each song in its music
// folder, and the saved analysis (beats, bars, sections and a timeline of
// musical features). Analysis is computed once (in the admin page) and saved
// on the hub, so songs are ready ahead of time; shared/song.ts reads it.

// Bump when the analysis algorithm changes, so old saved results are redone.
export const ANALYSIS_VERSION = 2;

export const AUDIO_TYPES: Record<string, string> = {
  ".mp3": "audio/mpeg",
  ".m4a": "audio/mp4",
  ".aac": "audio/aac",
  ".wav": "audio/wav",
  ".ogg": "audio/ogg",
  ".oga": "audio/ogg",
  ".opus": "audio/ogg",
  ".flac": "audio/flac",
};

export interface SavedAnalysis {
  version: number;
  bpm: number;
  duration: number;
  // Beat times in seconds from the start of the file.
  beats: number[];
  // The beats that start a bar (4 beats per bar), a subset of beats.
  downbeats: number[];
  // Parts of the song, in order; the first starts at 0. Sections with the
  // same label sound alike (a repeated chorus).
  sections: AnalysisSection[];
  // Feature frames per second: frame i describes time i / frameRate.
  frameRate: number;
  // One byte per frame per feature, base64. See FEATURES.
  features: Record<FeatureName, string>;
}

export interface AnalysisSection {
  start: number;
  // "A", "B", ...: same letter, similar music.
  label: string;
  // 0-1: how loud and busy this section is, relative to the song's biggest.
  energy: number;
}

// Per-frame features. All are 0-255 for 0-1, except pitchClass: 0-11 (C to B)
// or NO_PITCH when no single note stands out (silence, noise, dense chords).
//   loudness         perceived loudness, 1 = the song's loudest moments
//   bass/mid/treble  energy below 250 Hz, 250 Hz-4 kHz, above 4 kHz
//   onset            how sharply new sound arrives (hits, note starts)
//   pitchClass       the strongest note name, from a 12-note chroma
//   pitchConfidence  how much that note stands out from the others
//   register         spectral centroid: low (0) to bright (1) for this song
export const FEATURES = [
  "loudness",
  "bass",
  "mid",
  "treble",
  "onset",
  "pitchClass",
  "pitchConfidence",
  "register",
] as const;
export type FeatureName = (typeof FEATURES)[number];
export const NO_PITCH = 255;

export interface LibrarySong {
  // Content fingerprint (survives renames).
  id: string;
  file: string;
  title: string;
  bytes: number;
  analysis: SavedAnalysis | null;
  // May play on the public Listen page. Off unless the admin turns it on:
  // broadcasting purchased songs to the public needs licenses a purchase
  // doesn't include; public-domain and Creative Commons songs are fine.
  public: boolean;
  // Off: kept in the library, but the playlist and the show schedule skip it.
  enabled: boolean;
}

const MAX_BEATS = 50000;
const MAX_SECTIONS = 500;
// 30 minutes at 20 frames per second; longer files use fewer frames per
// second (analysis picks the rate) so a saved analysis stays well under 1 MB.
// What the song list sends: a summary instead of the whole analysis (which is
// tens of KB per song); fetch the full analysis per song when it's needed.
export interface AnalysisSummary {
  bpm: number;
  duration: number;
}

export type LibraryListing = Omit<LibrarySong, "analysis"> & { analysis: AnalysisSummary | null };

export function listing(song: LibrarySong): LibraryListing {
  const a = song.analysis;
  return { ...song, analysis: a ? { bpm: a.bpm, duration: a.duration } : null };
}

export const MAX_FRAMES = 36000;

// Feature frames for a song: one at 0 and one every 1/frameRate through the end.
export function frameCount(duration: number, frameRate: number): number {
  return Math.floor(duration * frameRate + 1e-6) + 1;
}

// Returns the analysis if it is well-formed and current, otherwise null.
export function parseAnalysis(value: unknown): SavedAnalysis | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const finite = (x: unknown, min: number, max: number): x is number =>
    typeof x === "number" && Number.isFinite(x) && x >= min && x <= max;
  if (v.version !== ANALYSIS_VERSION) return null;
  if (!finite(v.bpm, 10, 400) || !finite(v.duration, 0, 4 * 3600)) return null;
  const duration = v.duration;
  const times = (list: unknown): list is number[] =>
    Array.isArray(list) &&
    list.length <= MAX_BEATS &&
    list.every((t, i) => finite(t, 0, duration) && (i === 0 || t > (list[i - 1] as number)));
  if (!times(v.beats) || !times(v.downbeats)) return null;
  const beatSet = new Set(v.beats);
  if (!v.downbeats.every((t) => beatSet.has(t))) return null;

  if (!Array.isArray(v.sections) || v.sections.length < 1 || v.sections.length > MAX_SECTIONS) return null;
  const sections: AnalysisSection[] = [];
  for (const s of v.sections as unknown[]) {
    if (typeof s !== "object" || s === null) return null;
    const { start, label, energy } = s as Record<string, unknown>;
    const previous = sections[sections.length - 1];
    if (!finite(start, 0, duration) || (previous ? start <= previous.start : start !== 0)) return null;
    if (typeof label !== "string" || !/^[A-Z]$/.test(label) || !finite(energy, 0, 1)) return null;
    sections.push({ start, label, energy });
  }

  if (!Number.isInteger(v.frameRate) || !finite(v.frameRate, 1, 50)) return null;
  const frames = frameCount(duration, v.frameRate);
  if (frames > MAX_FRAMES) return null;
  if (typeof v.features !== "object" || v.features === null) return null;
  const given = v.features as Record<string, unknown>;
  const features = {} as Record<FeatureName, string>;
  for (const name of FEATURES) {
    const text = given[name];
    // Check the length before decoding so oversized input is refused cheaply.
    if (typeof text !== "string" || text.length !== base64Length(frames)) return null;
    const bytes = decodeBase64(text);
    if (!bytes || bytes.length !== frames) return null;
    if (name === "pitchClass" && !bytes.every((b) => b < 12 || b === NO_PITCH)) return null;
    features[name] = text;
  }

  return {
    version: ANALYSIS_VERSION,
    bpm: v.bpm,
    duration,
    beats: v.beats,
    downbeats: v.downbeats,
    sections,
    frameRate: v.frameRate,
    features,
  };
}

// Base64 by hand: the agent runs on Node versions without
// Uint8Array.fromBase64, and atob accepts sloppy input.
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const LOOKUP = new Int16Array(128).fill(-1);
for (let i = 0; i < ALPHABET.length; i++) LOOKUP[ALPHABET.charCodeAt(i)] = i;

export function base64Length(bytes: number): number {
  return Math.ceil(bytes / 3) * 4;
}

export function encodeBase64(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out +=
      ALPHABET[n >> 18]! +
      ALPHABET[(n >> 12) & 63]! +
      (b === undefined ? "=" : ALPHABET[(n >> 6) & 63]!) +
      (c === undefined ? "=" : ALPHABET[n & 63]!);
  }
  return out;
}

// Strict: padded, no whitespace. Returns null for anything else.
export function decodeBase64(text: string): Uint8Array | null {
  if (text.length % 4 !== 0) return null;
  const pad = text.endsWith("==") ? 2 : text.endsWith("=") ? 1 : 0;
  const out = new Uint8Array((text.length / 4) * 3 - pad);
  let o = 0;
  for (let i = 0; i < text.length; i += 4) {
    let n = 0;
    for (let j = 0; j < 4; j++) {
      const code = text.charCodeAt(i + j);
      const last = i + 4 === text.length;
      if (code === 61 && last && j >= 4 - pad) {
        n <<= 6;
        continue;
      }
      const d = code < 128 ? LOOKUP[code]! : -1;
      if (d < 0) return null;
      n = (n << 6) | d;
    }
    out[o++] = n >> 16;
    if (o < out.length) out[o++] = (n >> 8) & 255;
    if (o < out.length) out[o++] = n & 255;
  }
  return out;
}
